import {SleepAsync} from "js-vextensions";
import {AssertNotify, O, OnPopulated, RunInAction} from "web-vcore";
import godirect from "@vernier/godirect";
import {BreathProcessor} from "../../@Shared/Processors/BreathProcessor.js";
import {BeltPanel, BeltPanel_breathProcessor} from "../Monitor/BeltPanel.js";
import {BleClient} from "@capacitor-community/bluetooth-le";
import {WebBluetoothWrapper} from "../../../Utils/Capacitor/BLEPlugin_WebBluetoothWrapper.js";
import {ShowToast} from "../../../Utils/Bridge/Bridge_Native/General.js";
import {store} from "../../../Store/index.js";
import {AsFloat16} from "../../../Utils/General/Numbers.js";

export async function GetBeltDevice_BT(autoConnect_beltDeviceIDs: string[]) {
	await BleClient.initialize();

	// First, try to get previously granted devices
	const refoundDevices = await BleClient.getDevices(autoConnect_beltDeviceIDs);
	const refoundDevice = refoundDevices[0]; // just get 1st found-device (getDevices only gives device-ids, so can't check device names)
	if (refoundDevice) {
		console.log(ShowToast(`Connected (refound): ${refoundDevice.deviceId}${refoundDevice.name ? ` (${refoundDevice.name})` : ""}`));
		return new WebBluetoothWrapper(refoundDevice);
	}

	// If no existing device found, show the pairing dialog
	console.log("No previously paired device found, showing pairing dialog...");
	const device = await BleClient.requestDevice({
		//filters: [{namePrefix: "GDX-RB "}],
		namePrefix: "GDX-RB ",
		optionalServices: ["d91714ef-28b9-4f91-ba16-f0d9a604f112"],
	});

	console.log(ShowToast(`Connected (new): ${device?.deviceId} (${device?.name})`, "long", "bottom"));
	// since we're using BleClient (rather than web-bluetooth api directly), we need to wrap the device in a godirect wrapper
	return new WebBluetoothWrapper(device);
}

/** For options, see: https://github.com/VernierST/godirect-js/blob/main/src/godirect.js */
async function WrapBTDeviceInGDWrapper(nativeDevice: BluetoothDevice, options = {}) {
	return await godirect.createDevice(nativeDevice, options);
}

export type BeltConnectStatus = "disconnected" | "connecting" | "connected";

export class BeltInterface {
	@O accessor beltStatus: BeltConnectStatus = "disconnected";

	/**
	 * If true (the default), force samples received from the sensor are quantized down to f16
	 * *before* being delivered to force-listeners. This makes every downstream consumer
	 * (BreathProcessor, BreathComp, IndexedDB, detail-chart) operate on identical f16-representable
	 * values, so a roundtrip through storage never produces different processing results than happened
	 * during the live session. Set this to false only if you want to deliberately capture/observe raw
	 * f64 samples (e.g. for precision comparison testing).
	 */
	quantizeToF16 = true;

	requesters = new Set<any>(); // track components that request connection
	AddConnectRequester(requester: any) {
		const oldRequestersLength = this.requesters.size;
		this.requesters.add(requester);
		if (oldRequestersLength == 0) {
			this.Connect();
		}
	}
	RemoveConnectRequester(requester: any) {
		this.requesters.delete(requester);
		if (this.requesters.size == 0 && this.beltStatus != "disconnected") {
			this.Disconnect();
		}
	}

	device_bt?: BluetoothDevice;
	//device_bt?: WebBluetoothWrapper;
	// "any", since godirect has a Device wrapper-class; see: https://github.com/VernierST/godirect-js/blob/main/src/Device.js
	device?: object;

	async Connect() {
		if (this.beltStatus == "connected" || this.beltStatus == "connecting") return;
		RunInAction("BeltInterface.Connect", ()=>this.beltStatus = "connecting");

		//try {

		const device_bt = await GetBeltDevice_BT(store.main.tools.monitor.autoConnect_beltDevices.filter(a=>a.enabled).map(a=>a.id));
		if (!device_bt) {
			throw new Error("No Bluetooth device found/selected");
		}
		const device = await WrapBTDeviceInGDWrapper(device_bt);
		this.device_bt = device_bt;
		this.device = device;

		// Set up disconnect handler
		device.on("device-closed", ()=>{
			console.log(`Disconnected from ${device.name}`);
			RunInAction("BeltInterface.Connect.onDeviceClosed", ()=>this.beltStatus = "disconnected");
			this.device = undefined;
		});

		// Enable default sensors (likely includes respiration sensor)
		device.enableDefaultSensors();
		const enabledSensors = device.sensors.filter(s=>s.enabled);

		// Set up data handlers for enabled sensors
		enabledSensors.forEach(sensor=>{
			sensor.on("value-changed", sensor=>{
				this.handleSensorData(sensor);
			});
		});

		this.beltStatus = "connected";
		RunInAction("BeltInterface.Connect", ()=>this.beltStatus = "connected");
		console.log(`Connected to belt: ${device.name}`);

		/*}
		catch (error) {
			//console.error("Failed to connect to belt:", error);
			RunInAction("BeltInterface.Connect", ()=>this.beltStatus = "disconnected");
			this.device = undefined;
			throw error;
		}*/
	}

	async Disconnect() {
		//if (this.beltStatus == "disconnected") return;
		AssertNotify(this.beltStatus != "disconnected", "BeltInterface.Disconnect called while already marked as disconnected!");

		//try {

		if (this.device) {
			// Close the godirect device connection
			try {
				await this.device["close"]();
			} catch (error) {
				console.error("Error closing device:", error);
				AssertNotify(false, `Error closing device: ${error}`);
			}
			this.device = undefined;
		}
		RunInAction("BeltInterface.Disconnect", ()=>this.beltStatus = "disconnected");
		console.log("Belt disconnected successfully");

		/*}
		catch (error) {
			//console.error("Failed to disconnect from belt:", error);
			// Force status update even if disconnect failed
			RunInAction("BeltInterface.Disconnect", ()=>this.beltStatus = "disconnected");
			this.device = undefined;
			throw error;
		}*/
	}

	//samplesToProcess = [] as number[]; // atm we only read/store the "Force" values
	//activeProcessors = [] as BreathProcessor[];
	forceListeners = [] as ((sample: number)=>void)[]; // listeners for force samples

	private lastForceSampleTime: number|n;
	private handleSensorData(sensor: GDSensor) {
		if (sensor.name == "Force") {
			// only log after gaps in data (as way for user to confirm that data's being received, without flooding console)
			if (this.lastForceSampleTime == null || Date.now() - this.lastForceSampleTime > 500) {
				console.log("Respiration data received (after >500ms gap):", {
					name: sensor.name,
					value: sensor.value,
					unit: sensor.unit,
					timestamp: Date.now()
				});
			}
			this.lastForceSampleTime = Date.now();

			//this.samplesToProcess.push(sensor.value);
			/*for (const processor of this.activeProcessors) {
				processor.AddBeltForceSample(sensor.value);
				if (processor == BeltPanel_breathProcessor && BeltGraph_self) {
					BeltGraph_self.UpdateChart();
				}
			}*/
			// Quantize the raw f64 sensor value down to f16 (if enabled) before handing it to listeners,
			// so that every downstream consumer (BreathProcessor, BreathComp, IndexedDB) sees identical
			// f16-representable values and a roundtrip through storage never changes processing results.
			const sampleForListeners = this.quantizeToF16 ? AsFloat16(sensor.value) : sensor.value;
			for (const listener of this.forceListeners) {
				listener(sampleForListeners);
			}
		}
	}
}
export let currentBelt: BeltInterface;
OnPopulated(()=>{
	currentBelt = new BeltInterface();
});

export type GDSensor = {
	name: string;
	timestamp: number;
	unit: string;
	value: number;
};